import React, { useMemo, useState } from 'react';
import styled from 'styled-components';
import { Plus, Edit3, Trash2, RefreshCw, Newspaper } from 'lucide-react';
import { backofficeApi, formatNumber, extractErrorMessage } from '@kardume/services';
import { useNotification } from '@kardume/hooks';
import type { FeedDto, FeedRequestDto, FeedStatus, FeedType } from '@kardume/types';
import {
  PageHeader,
  StatCard,
  Table,
  TableContainer,
  Badge,
  Button,
  ActionButton,
  SearchInput,
  Select,
  TextInput,
  RichTextInput,
  Switch,
  Modal,
  Pagination,
} from '@kardume/ui';
import { useAsync } from '../utils/useAsync';
import { toLocalInput, fromLocalInput } from '../utils/datetime';
import { useDebouncedValue } from '../utils/useDebouncedValue';
import DataState from '../components/DataState';

const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 1rem;
  margin-bottom: 1.25rem;

  @media (max-width: 900px) {
    grid-template-columns: 1fr;
  }
`;

const Toolbar = styled.div`
  display: flex;
  gap: 0.75rem;
  margin-bottom: 1rem;
  flex-wrap: wrap;
  align-items: center;

  > *:first-child {
    flex: 1;
    min-width: 240px;
  }
`;

const RowActions = styled.div`
  display: flex;
  gap: 0.5rem;
  justify-content: flex-end;
`;

const TitleCell = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;

  strong {
    font-size: 0.9rem;
    color: ${(p) => p.theme.textPrimary};
  }

  span {
    font-size: 0.78rem;
    color: ${(p) => p.theme.textMuted};
  }
`;

const FieldRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 0.75rem;
  margin-top: 0.75rem;
`;

const FieldHint = styled.p`
  margin: 0.45rem 0 0;
  font-size: 0.78rem;
  line-height: 1.45;
  color: ${(p) => p.theme.textMuted};
`;

const ConfirmText = styled.p`
  margin: 0;
  font-size: 0.9rem;
  line-height: 1.5;
  color: ${(p) => p.theme.textSecondary};

  strong {
    color: ${(p) => p.theme.textPrimary};
  }
`;

const STATUS_FILTER = [
  { value: '', label: 'Todas as situações' },
  { value: 'DRAFT', label: 'Rascunho' },
  { value: 'PUBLISHED', label: 'Publicada' },
  { value: 'ARCHIVED', label: 'Arquivada' },
];

const STATUS_OPTIONS = [
  { value: 'DRAFT', label: 'Rascunho' },
  { value: 'PUBLISHED', label: 'Publicada' },
  { value: 'ARCHIVED', label: 'Arquivada' },
];

const TYPE_OPTIONS = [
  { value: 'ARTICLE', label: 'Matéria' },
  { value: 'HIGHLIGHT', label: 'Destaque' },
  { value: 'NOTICE', label: 'Aviso' },
];

const TYPE_LABEL: Record<FeedType, string> = {
  ARTICLE: 'Matéria',
  HIGHLIGHT: 'Destaque',
  NOTICE: 'Aviso',
};

const PAGE_SIZE_OPTIONS = [10, 20, 50, 100];

const EMPTY_FORM: FeedRequestDto = {
  title: '',
  body: '',
  type: 'ARTICLE',
  status: 'DRAFT',
  localityId: null,
  categoryId: null,
  subtitle: '',
  coverUrl: '',
  seoTitle: '',
  seoDescription: '',
  sortOrder: 0,
  publishedFrom: null,
  publishedUntil: null,
  authorName: '',
};

/** Texto puro do corpo, só para a prévia da tabela. */
function plainText(html: string): string {
  return html.replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim();
}

const FeedsPage: React.FC = () => {
  const { showNotification } = useNotification();
  const [search, setSearch] = useState<string>('');
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [cityFilter, setCityFilter] = useState<string>('');
  const [page, setPage] = useState<number>(0);
  const [pageSize, setPageSize] = useState<number>(20);
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [form, setForm] = useState<FeedRequestDto>(EMPTY_FORM);
  const [publishWindow, setPublishWindow] = useState({ from: '', until: '' });
  const [submitting, setSubmitting] = useState<boolean>(false);
  const [deleting, setDeleting] = useState<FeedDto | null>(null);
  const [removing, setRemoving] = useState<boolean>(false);

  const debouncedSearch = useDebouncedValue(search.trim());

  const { data, loading, error, reload } = useAsync(
    () =>
      backofficeApi.listFeeds({
        page,
        size: pageSize,
        query: debouncedSearch || undefined,
        status: (statusFilter || undefined) as FeedStatus | undefined,
        localityId: cityFilter || undefined,
        sort: 'sortOrder,asc',
      }),
    [page, pageSize, debouncedSearch, statusFilter, cityFilter]
  );

  // Cidades e categorias são catálogos curtos: uma leitura só, reaproveitada
  // pelos filtros e pelo formulário. Buscar de novo ao abrir o modal faria a
  // tela piscar a cada edição.
  const { data: localityPage } = useAsync(
    () => backofficeApi.listLocalities({ page: 0, size: 100, active: true, sort: 'name,asc' }),
    []
  );
  const { data: categoryPage } = useAsync(
    () => backofficeApi.listCategories({ page: 0, size: 100, sort: 'name,asc' }),
    []
  );

  const cityOptions = useMemo(
    () => [
      { value: '', label: 'Todas as cidades' },
      ...(localityPage?.content ?? []).map((l) => ({ value: l.id, label: `${l.name}, ${l.state}` })),
    ],
    [localityPage]
  );

  const categoryOptions = useMemo(
    () => [
      { value: '', label: 'Página da cidade (sem categoria)' },
      ...(categoryPage?.content ?? []).map((c) => ({ value: c.id, label: c.name })),
    ],
    [categoryPage]
  );

  const feeds = data?.content ?? [];
  const totalItems = data?.totalElements ?? 0;
  const totalPages = data?.totalPages ?? 0;

  const resetToFirstPage = (): void => setPage(0);

  const openCreate = (): void => {
    setEditingId(null);
    setForm(EMPTY_FORM);
    setPublishWindow({ from: '', until: '' });
    setIsOpen(true);
  };

  const openEdit = (feed: FeedDto): void => {
    setEditingId(feed.id);
    setForm({
      title: feed.title,
      body: feed.body,
      type: feed.type,
      status: feed.status,
      localityId: feed.localityId,
      categoryId: feed.categoryId,
      subtitle: feed.subtitle ?? '',
      coverUrl: feed.coverUrl ?? '',
      seoTitle: feed.seoTitle ?? '',
      seoDescription: feed.seoDescription ?? '',
      sortOrder: feed.sortOrder,
      publishedFrom: feed.publishedFrom,
      publishedUntil: feed.publishedUntil,
      authorName: feed.authorName ?? '',
    });
    setPublishWindow({ from: toLocalInput(feed.publishedFrom), until: toLocalInput(feed.publishedUntil) });
    setIsOpen(true);
  };

  const update = <K extends keyof FeedRequestDto>(key: K, value: FeedRequestDto[K]): void => {
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  const submit = async (): Promise<void> => {
    if (!form.title.trim()) {
      showNotification('Informe o título da matéria.', 'error');
      return;
    }
    if (!plainText(form.body)) {
      showNotification('Escreva o conteúdo da matéria.', 'error');
      return;
    }

    const publishedFrom = fromLocalInput(publishWindow.from);
    const publishedUntil = fromLocalInput(publishWindow.until);
    // Avisar aqui evita a ida e volta: janela invertida é conteúdo que nunca
    // aparece, e o backend recusa com FEED_INVALID_WINDOW.
    if (publishedFrom && publishedUntil && new Date(publishedUntil) < new Date(publishedFrom)) {
      showNotification('O fim da publicação deve ser depois do início.', 'error');
      return;
    }

    setSubmitting(true);
    try {
      const payload: FeedRequestDto = {
        ...form,
        title: form.title.trim(),
        localityId: form.localityId || null,
        categoryId: form.categoryId || null,
        publishedFrom,
        publishedUntil,
      };
      if (editingId) {
        await backofficeApi.updateFeed(editingId, payload);
        showNotification('Matéria atualizada com sucesso.', 'success');
      } else {
        await backofficeApi.createFeed(payload);
        showNotification('Matéria criada com sucesso.', 'success');
      }
      setIsOpen(false);
      reload();
    } catch (err) {
      showNotification(extractErrorMessage(err, 'Não foi possível salvar a matéria.'), 'error');
    } finally {
      setSubmitting(false);
    }
  };

  /** Publicar/despublicar na própria linha, como o toggle de Localidades. */
  const togglePublished = async (feed: FeedDto): Promise<void> => {
    const nextStatus: FeedStatus = feed.status === 'PUBLISHED' ? 'DRAFT' : 'PUBLISHED';
    try {
      await backofficeApi.updateFeed(feed.id, {
        title: feed.title,
        body: feed.body,
        type: feed.type,
        status: nextStatus,
        localityId: feed.localityId,
        categoryId: feed.categoryId,
        subtitle: feed.subtitle,
        coverUrl: feed.coverUrl,
        seoTitle: feed.seoTitle,
        seoDescription: feed.seoDescription,
        sortOrder: feed.sortOrder,
        publishedFrom: feed.publishedFrom,
        publishedUntil: feed.publishedUntil,
        authorName: feed.authorName,
      });
      showNotification(nextStatus === 'PUBLISHED' ? 'Matéria publicada.' : 'Matéria voltou para rascunho.', 'success');
      reload();
    } catch (err) {
      showNotification(extractErrorMessage(err, 'Não foi possível alterar a situação.'), 'error');
    }
  };

  const confirmRemove = async (): Promise<void> => {
    if (!deleting) return;
    setRemoving(true);
    try {
      await backofficeApi.deleteFeed(deleting.id);
      showNotification('Matéria excluída.', 'success');
      setDeleting(null);
      reload();
    } catch (err) {
      showNotification(extractErrorMessage(err, 'Não foi possível excluir a matéria.'), 'error');
    } finally {
      setRemoving(false);
    }
  };

  return (
    <>
      <PageHeader
        title="Feeds e matérias"
        subtitle="Conteúdo editorial das páginas de cidade. Sem cidade, a matéria vale para todas; sem categoria, ela abre a página da cidade."
        actions={
          <Button variant="primary" onClick={openCreate}>
            <Plus size={16} /> Nova matéria
          </Button>
        }
      />

      <StatsGrid>
        <StatCard label="Total (respeita o filtro)" value={formatNumber(totalItems)} />
        <StatCard label="No ar (nesta página)" value={formatNumber(feeds.filter((f) => f.visible).length)} />
        <StatCard label="Rascunhos (nesta página)" value={formatNumber(feeds.filter((f) => f.status === 'DRAFT').length)} />
      </StatsGrid>

      <Toolbar>
        <SearchInput
          placeholder="Buscar por título ou endereço..."
          value={search}
          onChange={(e) => {
            setSearch(e.target.value);
            resetToFirstPage();
          }}
        />
        <Select
          options={STATUS_FILTER}
          value={statusFilter}
          onChange={(e) => {
            setStatusFilter(e.target.value);
            resetToFirstPage();
          }}
        />
        <Select
          options={cityOptions}
          value={cityFilter}
          onChange={(e) => {
            setCityFilter(e.target.value);
            resetToFirstPage();
          }}
        />
        <ActionButton title="Atualizar" onClick={reload}>
          <RefreshCw size={16} />
        </ActionButton>
      </Toolbar>

      <DataState
        loading={loading}
        error={error}
        isEmpty={feeds.length === 0}
        emptyTitle="Nenhuma matéria encontrada"
        emptyDescription="Escreva a primeira matéria para a página da cidade sair do ar como lista pura de lojas."
        onRetry={reload}
      >
        <TableContainer>
          <Table>
            <thead>
              <tr>
                <th>Matéria</th>
                <th style={{ width: '1%', whiteSpace: 'nowrap' }}>Onde aparece</th>
                <th style={{ width: '1%', whiteSpace: 'nowrap' }}>Tipo</th>
                <th style={{ width: '1%', whiteSpace: 'nowrap', textAlign: 'right' }}>Situação</th>
                <th style={{ width: '1%', whiteSpace: 'nowrap', textAlign: 'right' }}>Ações</th>
              </tr>
            </thead>
            <tbody>
              {feeds.map((feed) => (
                <tr key={feed.id}>
                  <td>
                    <TitleCell>
                      <Newspaper size={14} />
                      <div>
                        <strong>{feed.title}</strong>
                        <br />
                        <span>/{feed.slug}</span>
                      </div>
                    </TitleCell>
                  </td>
                  <td style={{ whiteSpace: 'nowrap' }}>
                    {feed.localityLabel ?? 'Todas as cidades'}
                    {feed.categoryName ? ` · ${feed.categoryName}` : ''}
                  </td>
                  <td style={{ whiteSpace: 'nowrap' }}>{TYPE_LABEL[feed.type]}</td>
                  <td style={{ textAlign: 'right' }}>
                    <Badge tone={feed.visible ? 'success' : feed.status === 'PUBLISHED' ? 'warning' : 'secondary'}>
                      {feed.visible ? 'No ar' : feed.status === 'PUBLISHED' ? 'Agendada' : 'Rascunho'}
                    </Badge>
                  </td>
                  <td>
                    <RowActions>
                      <Switch
                        checked={feed.status === 'PUBLISHED'}
                        onChange={() => togglePublished(feed)}
                        aria-label={feed.status === 'PUBLISHED' ? 'Voltar para rascunho' : 'Publicar matéria'}
                      />
                      <ActionButton aria-label="Editar matéria" title="Editar" onClick={() => openEdit(feed)}>
                        <Edit3 size={16} />
                      </ActionButton>
                      <ActionButton
                        aria-label="Excluir matéria"
                        title="Excluir"
                        color="#DC2626"
                        onClick={() => setDeleting(feed)}
                      >
                        <Trash2 size={16} />
                      </ActionButton>
                    </RowActions>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        </TableContainer>
      </DataState>

      {totalPages > 1 && (
        <Pagination
          currentPage={page + 1}
          totalPages={totalPages}
          totalItems={totalItems}
          pageSize={pageSize}
          pageSizeOptions={PAGE_SIZE_OPTIONS}
          disabled={loading}
          onPageChange={(next) => setPage(next - 1)}
          onPageSizeChange={(size) => {
            setPageSize(size);
            resetToFirstPage();
          }}
        />
      )}

      <Modal
        isOpen={isOpen}
        onClose={() => setIsOpen(false)}
        title={editingId ? 'Editar matéria' : 'Nova matéria'}
        width="720px"
        footer={
          <>
            <Button variant="ghost" onClick={() => setIsOpen(false)} disabled={submitting}>
              Cancelar
            </Button>
            <Button variant="primary" onClick={submit} isLoading={submitting}>
              Salvar
            </Button>
          </>
        }
      >
        <TextInput
          label="Título"
          value={form.title}
          onChange={(e) => update('title', e.target.value)}
          placeholder="Ex.: Onde tomar café em Toledo"
        />
        <TextInput
          label="Subtítulo"
          value={form.subtitle ?? ''}
          onChange={(e) => update('subtitle', e.target.value)}
          placeholder="Uma linha que dá vontade de ler o resto"
          containerStyle={{ marginTop: '0.75rem' }}
        />
        <FieldRow>
          <Select
            label="Cidade"
            options={cityOptions}
            value={form.localityId ?? ''}
            onChange={(e) => update('localityId', e.target.value || null)}
          />
          <Select
            label="Categoria"
            options={categoryOptions}
            value={form.categoryId ?? ''}
            onChange={(e) => update('categoryId', e.target.value || null)}
          />
        </FieldRow>
        <FieldHint>
          Sem cidade a matéria aparece em todas as cidades; sem categoria ela abre a página da
          cidade, e não a página de uma categoria.
        </FieldHint>

        <FieldRow>
          <Select
            label="Tipo"
            options={TYPE_OPTIONS}
            value={form.type}
            onChange={(e) => update('type', e.target.value as FeedType)}
          />
          <Select
            label="Situação"
            options={STATUS_OPTIONS}
            value={form.status}
            onChange={(e) => update('status', e.target.value as FeedStatus)}
          />
        </FieldRow>

        <div style={{ marginTop: '0.75rem' }}>
          <RichTextInput
            label="Conteúdo"
            value={form.body}
            onChange={(html) => update('body', html)}
          />
        </div>

        <FieldRow>
          <TextInput
            label="Publicar a partir de"
            type="datetime-local"
            value={publishWindow.from}
            onChange={(e) => setPublishWindow((prev) => ({ ...prev, from: e.target.value }))}
          />
          <TextInput
            label="Publicar até"
            type="datetime-local"
            value={publishWindow.until}
            onChange={(e) => setPublishWindow((prev) => ({ ...prev, until: e.target.value }))}
          />
        </FieldRow>
        <FieldHint>
          Em branco significa sem limite. Uma matéria publicada com início no futuro fica
          agendada - ela não aparece no site antes da data.
        </FieldHint>

        <FieldRow>
          <TextInput
            label="Título de SEO"
            value={form.seoTitle ?? ''}
            onChange={(e) => update('seoTitle', e.target.value)}
            placeholder="Cai para o título quando vazio"
          />
          <TextInput
            label="Autor"
            value={form.authorName ?? ''}
            onChange={(e) => update('authorName', e.target.value)}
            placeholder="Quem assina"
          />
        </FieldRow>
        <TextInput
          label="Descrição de SEO"
          value={form.seoDescription ?? ''}
          onChange={(e) => update('seoDescription', e.target.value)}
          placeholder="Resumo que aparece no Google"
          containerStyle={{ marginTop: '0.75rem' }}
        />
        <FieldRow>
          <TextInput
            label="Imagem de capa (URL)"
            value={form.coverUrl ?? ''}
            onChange={(e) => update('coverUrl', e.target.value)}
            placeholder="https://..."
          />
          <TextInput
            label="Ordem"
            type="number"
            value={String(form.sortOrder ?? 0)}
            onChange={(e) => update('sortOrder', Number(e.target.value) || 0)}
          />
        </FieldRow>
      </Modal>

      <Modal
        isOpen={deleting !== null}
        onClose={() => setDeleting(null)}
        title="Excluir matéria"
        width="420px"
        footer={
          <>
            <Button variant="ghost" onClick={() => setDeleting(null)} disabled={removing}>
              Cancelar
            </Button>
            <Button variant="danger" onClick={confirmRemove} isLoading={removing}>
              Excluir
            </Button>
          </>
        }
      >
        <ConfirmText>
          Tem certeza que deseja excluir <strong>{deleting?.title ?? ''}</strong>? O endereço{' '}
          <strong>/{deleting?.slug ?? ''}</strong> deixará de responder.
        </ConfirmText>
      </Modal>
    </>
  );
};

export default FeedsPage;
